import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import ActivityLayout from "@/layout/ActivityLayout";
import { getStockActivityInfo, useRebateCommissionFlag } from "@/service/activity.service";
import dayjs from "dayjs";

import clsx from "clsx";
import { deleteUnread, useCouponUnread } from "@/service/coupon.service";
import TaskListView from "@/containers/rewards/TaskListView";
import dynamic from "next/dynamic";
import Spin from "@/components/Spin";
const TabLoading = () => (
	<div className="py-6 flex flex-col items-center justify-center text-sm text-949494">
		<Spin loading />
	</div>
);
const RewardsPageView = dynamic(() => import("@/containers/rewards/RewardsPageView"), {
	loading: TabLoading,
});
const CouponView = dynamic(() => import("@/containers/rewards/Coupon"), { ssr: false, loading: TabLoading });
const RebateCommission = dynamic(() => import("../rebate-commission"), {
	loading: TabLoading,
});

enum UIState {
	task,
	reward,
	coupon,
	rebate,
}
interface RewardsViewProps {
	isActivityActive: boolean;
	tab?: string;
}
export default function RewardsView({ isActivityActive, tab }: RewardsViewProps) {
	const { t } = useTranslation("activities");
	const router = useRouter();
	const initialUiState = useMemo(() => {
		const queryTab = router.query.tab ?? tab;
		if (queryTab !== undefined) {
			return Number(queryTab) as UIState;
		}
		return UIState.task;
	}, [router.query.tab, tab]);
	const [uiState, setUiState] = useState<UIState>(initialUiState);
	const [loadedTabs, setLoadedTabs] = useState<Record<UIState, boolean>>(() => ({
		[UIState.task]: initialUiState === UIState.task,
		[UIState.reward]: initialUiState === UIState.reward,
		[UIState.coupon]: initialUiState === UIState.coupon,
		[UIState.rebate]: initialUiState === UIState.rebate,
	}));
	const { data: unReadData, isLoading: unreadLoading, mutate } = useCouponUnread();
	const { data: rebateCommissionFlag } = useRebateCommissionFlag();
	const showRateOption = Boolean(rebateCommissionFlag);

	const options = useMemo(() => {
		const initial = [
			{
				label: t("REWARD_task-list"),
				value: UIState.task,
			},
			{
				label: t("REWARD_rewards"),
				value: UIState.reward,
			},
			{
				label: t("REWARD_coupons"),
				value: UIState.coupon,
			},
		];
		const rebate = {
			label: t("REWARD_rebate"),
			value: UIState.rebate,
		};

		return showRateOption ? [...initial, rebate] : [...initial];
	}, [showRateOption, t]);

	const hasUnreadCoupon = useMemo(() => {
		if (unreadLoading) return false;
		return Boolean(unReadData?.length);
	}, [unReadData, unreadLoading]);

	const showHot = useCallback((val: UIState) => val === UIState.coupon && hasUnreadCoupon, [hasUnreadCoupon]);

	const onTabChange = (val: UIState) => {
		setUiState(val);
		if (router.isReady) {
			router.replace(
				{
					pathname: router.pathname,
					query: {
						...router.query,
						tab: String(val),
					},
				},
				undefined,
				{ shallow: true, scroll: false },
			);
		}
	};

	useEffect(() => {
		if (!router.isReady) return;
		const queryTab = router.query.tab;
		if (queryTab === undefined) return;
		const normalized = Array.isArray(queryTab) ? queryTab[0] : queryTab;
		const nextState = Number(normalized) as UIState;
		if (Number.isNaN(nextState)) return;
		setUiState((prev) => (prev === nextState ? prev : nextState));
	}, [router.isReady, router.query.tab]);

	useEffect(() => {
		setLoadedTabs((prev) => {
			if (prev[uiState]) return prev;
			return {
				...prev,
				[uiState]: true,
			};
		});
	}, [uiState]);

	const lastClearedUnreadType = useRef<number | null>(null);

	useEffect(() => {
		if (!hasUnreadCoupon) {
			lastClearedUnreadType.current = null;
		}
	}, [hasUnreadCoupon]);

	useEffect(() => {
		if (uiState !== UIState.coupon) return;
		const unReadType = unReadData?.[0]?.unReadType;
		if (!unReadType) return;
		if (lastClearedUnreadType.current === unReadType) return;
		let cancelled = false;
		deleteUnread(unReadType)
			.then(() => {
				if (cancelled) return;
				lastClearedUnreadType.current = unReadType;
				mutate();
			})
			.catch(() => {
				lastClearedUnreadType.current = null;
			});
		return () => {
			cancelled = true;
		};
	}, [uiState, unReadData, mutate]);

	return (
		<>
			<Head>
				<title>{t("my-rewards")}</title>
				<link
					rel="preload"
					href="https://web-cdn.rockflow.tech/h5/activities/reward/coupon-card-bg-light.png"
					as="image"
				/>
				<link
					rel="preload"
					href="https://web-cdn.rockflow.tech/h5/activities/reward/coupon-card-bg-dark.png"
					as="image"
				/>
			</Head>

			<div className="w-full h-full mx-auto max-w-screen-md flex flex-col">
				<div className="w-full overflow-x-auto hidden-scroll-bar flex-shrink-0">
					<div className="flex items-center flex-nowrap overflow-x-auto hidden-scroll-bar">
						{options.map((option) => (
							<div
								className={clsx(
									"flex-1 px-3.5 text-center text-sm font-semibold py-2.5 transition-colors duration-300 whitespace-nowrap",
									option.value === uiState
										? "border-b-2 border-b-dark dark:border-b-white text-dark dark:text-white"
										: "text-717275 dark:text-949494 border-b-1 border-b-eaeaea dark:border-b-[#333]",
								)}
								key={option.label}
								onClick={() => onTabChange(option.value)}
							>
								<span className="relative">
									{option.label}
									{showHot(option.value) && (
										<svg
											className="absolute -right-1.5 top-1"
											xmlns="http://www.w3.org/2000/svg"
											width="5"
											height="5"
											viewBox="0 0 5 5"
											fill="none"
										>
											<circle cx="2.5" cy="2.5" r="2.5" fill="#DE5275" />
										</svg>
									)}
								</span>
							</div>
						))}
					</div>
				</div>

				<div className="flex-1 overflow-y-auto hidden-scroll-bar relative">
					{loadedTabs[UIState.task] && (
						<div className={clsx("h-full", uiState === UIState.task ? "block" : "hidden")}>
							<TaskListView isActivityActive={isActivityActive} />
						</div>
					)}
					{loadedTabs[UIState.reward] && (
						<div className={clsx("h-full", uiState === UIState.reward ? "block" : "hidden")}>
							<RewardsPageView isActivityActive={isActivityActive} />
						</div>
					)}
					{loadedTabs[UIState.coupon] && (
						<div className={clsx("h-full", uiState === UIState.coupon ? "block" : "hidden")}>
							<CouponView
								recordMutate={async () => {
									await mutate();
								}}
							/>
						</div>
					)}
					{showRateOption && loadedTabs[UIState.rebate] && (
						<div className={clsx("h-full", uiState === UIState.rebate ? "block" : "hidden")}>
							<div className="h-full">
								<RebateCommission />
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	let isActivityActive = false;
	try {
		const data = await getStockActivityInfo();
		const isStockRebateActivity = dayjs(Date.now()).isAfter(data.startTime) && dayjs(Date.now()).isBefore(data.endTime);
		if (isStockRebateActivity && data.enabled) {
			isActivityActive = true;
		}
	} catch (error) {}
	return {
		props: {
			...(await serverSideTranslations(context.locale as string, ["nft", "error", "common", "share", "activities"])),
			isActivityActive,
			...context.query,
		},
	};
};

RewardsView.getLayout = function getLayout(page: ReactElement) {
	return <ActivityLayout>{page}</ActivityLayout>;
};
